import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid,
  Card,
  TextField,
  Button,
  Alert,
  useTheme,
  CircularProgress
} from '@mui/material';
import { 
  WhatsApp as WhatsAppIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  Language as LanguageIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../utils/LanguageContext';
import Layout from '../components/layout/Layout';
import contactInfo from '../config/contact';
import QuickOrderSection from '../components/home/QuickOrderSection';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailjs';

const ContactPage: React.FC = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const theme = useTheme();
  
  useEffect(() => {
    // Initialize EmailJS with your public key
    emailjs.init(EMAILJS_CONFIG.PUBLIC_KEY);
    
    // Debug environment variables
    console.log('EmailJS Environment Variables Status (ContactPage):', {
      'SERVICE_ID': Boolean(EMAILJS_CONFIG.SERVICE_ID),
      'TEMPLATE_ID': Boolean(EMAILJS_CONFIG.TEMPLATE_ID),
      'PUBLIC_KEY': Boolean(EMAILJS_CONFIG.PUBLIC_KEY),
      'ADMIN_EMAIL': EMAILJS_CONFIG.ADMIN_EMAIL
    });
    
    // Check for missing required configs
    if (!EMAILJS_CONFIG.SERVICE_ID || !EMAILJS_CONFIG.TEMPLATE_ID || !EMAILJS_CONFIG.PUBLIC_KEY) {
      console.warn('EmailJS configuration incomplete - emails will not be sent. Check your environment variables.');
    }
  }, []);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errors, setErrors] = useState<{[key: string]: string}>({});

  const whatsappUrl = `https://wa.me/${contactInfo.whatsapp.number}?text=${encodeURIComponent(contactInfo.whatsapp.message[language as keyof typeof contactInfo.whatsapp.message])}`;

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('contact.form.errors.nameRequired');
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = t('contact.form.errors.phoneRequired');
    } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
      newErrors.phone = t('contact.form.errors.phoneInvalid');
    }
    
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.form.errors.emailInvalid');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact.form.errors.messageRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitStatus('submitting');
    
    try {
      // Send email to admin using EmailJS
      try {
        console.log('Sending email notification to admin');
        
        // Check if admin email is available, otherwise use a fallback
        const adminEmail = EMAILJS_CONFIG.ADMIN_EMAIL || 'blackrc3636@gmail.com';
        
        if (!adminEmail) {
          throw new Error('Admin email is not configured');
        }
        
        console.log('Using admin email:', adminEmail);

        const emailParams = {
          to_email: adminEmail,
          to_name: 'Admin',
          recipient: adminEmail,
          email: adminEmail,
          from_name: 'Gamifly Website Contact Form',
          subject: 'New Contact Form Submission',
          name: formData.name,
          phone: formData.phone,
          sender_email: formData.email || 'Not provided',
          message: formData.message,
          message_html: `
            <h2>New Contact Form Submission</h2>
            <p><strong>Name:</strong> ${formData.name}</p>
            <p><strong>Phone:</strong> ${formData.phone}</p>
            <p><strong>Email:</strong> ${formData.email || 'Not provided'}</p>
            <p><strong>Message:</strong></p>
            <p>${formData.message}</p>
          `,
        };

        console.log('Email parameters:', emailParams);

        const response = await emailjs.send(
          EMAILJS_CONFIG.SERVICE_ID,
          EMAILJS_CONFIG.TEMPLATE_ID,
          emailParams
        );
        
        console.log('Email sent successfully:', response);
      } catch (emailError: any) {
        console.error('Error sending email notification:', emailError);
        console.error('Error details:', emailError.text);
        throw emailError; // Re-throw to be caught by outer try-catch
      }
      
      setSubmitStatus('success');
      setFormData({ name: '', phone: '', email: '', message: '' });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }
  };

  const handleInputChange = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const contactMethods = [
    {
      icon: <WhatsAppIcon />,
      title: t('contact.info.whatsapp'),
      description: t('contact.info.whatsappDesc'),
      value: contactInfo.whatsapp.number,
      action: whatsappUrl,
      color: '#25D366'
    },
    {
      icon: <PhoneIcon />,
      title: t('contact.info.phone'),
      description: t('contact.info.phoneDesc'),
      value: contactInfo.company.phone,
      action: `tel:${contactInfo.company.phone}`,
      color: '#4A90E2'
    },
    {
      icon: <EmailIcon />,
      title: t('contact.info.email'),
      description: t('contact.info.emailDesc'),
      value: contactInfo.company.email,
      action: `mailto:${contactInfo.company.email}`,
      color: '#66BB6A'
    }
  ];

  const businessInfo = [
    {
      icon: <TimeIcon />,
      title: t('contact.info.hours'),
      details: [
        t('contact.info.hoursWeekday'),
        t('contact.info.hoursSaturday'),
        t('contact.info.hoursSunday'),
        t('contact.info.hoursEmergency')
      ]
    },
    {
      icon: <LanguageIcon />,
      title: t('contact.info.languages'),
      details: [
        t('contact.info.langEnglish'),
        t('contact.info.langCantonese'),
        t('contact.info.langMandarin'),
        t('contact.info.langNote')
      ]
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <Box
        sx={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          py: { xs: 10, md: 15 },
          background: 'linear-gradient(135deg, rgba(18,18,18,0.95) 0%, rgba(26,32,48,0.95) 100%)',
          overflow: 'hidden',
          mb: 8,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url(/pattern-bg.png) repeat',
            opacity: 0.1,
            zIndex: 0,
          },
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box textAlign="center">
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 700,
                fontSize: { xs: '2.5rem', md: '4rem' },
                background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3,
              }}
            >
              {t('contact.title')}
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{
                maxWidth: 800,
                mx: 'auto',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              {t('contact.description')}
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={6}>
          {/* Contact Methods */}
          <Grid size={{ xs: 12, lg: 5 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
              {t('contact.info.title')}
            </Typography>
            
            <Grid container spacing={3}>
              {contactMethods.map((method, index) => (
                <Grid size={12} key={index}>
                  <Card
                    component="a"
                    href={method.action}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      p: 3,
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      textDecoration: 'none',
                      color: 'inherit',
                      display: 'block',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 25px ${method.color}30`,
                        border: `1px solid ${method.color}50`,
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          background: `${method.color}20`,
                          color: method.color,
                          mr: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {method.icon}
                      </Box>
                      <Box>
                        <Typography variant="h6" gutterBottom>
                          {method.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          {method.description}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {method.value}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Business Information */}
            <Box sx={{ mt: 6 }}>
              {businessInfo.map((info, index) => (
                <Card
                  key={index}
                  sx={{
                    p: 3,
                    mb: 3,
                    background: 'rgba(255, 255, 255, 0.05)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        background: 'rgba(74, 144, 226, 0.2)',
                        color: '#4A90E2',
                        mr: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {info.icon}
                    </Box>
                    <Typography variant="h6">
                      {info.title}
                    </Typography>
                  </Box>
                  {info.details.map((detail, detailIndex) => (
                    <Typography
                      key={detailIndex}
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, ml: 5 }}
                    >
                      {detail}
                    </Typography>
                  ))}
                </Card>
              ))}
            </Box>
          </Grid>

          {/* Contact Form */}
          <Grid size={{ xs: 12, lg: 7 }}>
            <Card
              component="form"
              onSubmit={handleSubmit}
              sx={{
                p: 4,
                background: 'rgba(255, 255, 255, 0.05)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: 3,
                height: '100%',
              }}
            >
              <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
                {t('contact.form.title')}
              </Typography>
              
              {submitStatus === 'success' && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 3 }}
                  icon={<CheckIcon />}
                >
                  {t('contact.form.success')}
                </Alert>
              )}
              
              {submitStatus === 'error' && (
                <Alert severity="error" sx={{ mb: 3 }}>
                  {t('contact.form.error')}
                </Alert>
              )}

              <Grid container spacing={3}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('contact.form.name')}
                    value={formData.name}
                    onChange={handleInputChange('name')}
                    error={!!errors.name}
                    helperText={errors.name}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label={t('contact.form.phone')}
                    value={formData.phone}
                    onChange={handleInputChange('phone')}
                    error={!!errors.phone}
                    helperText={errors.phone}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('contact.form.email')}
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange('email')}
                    error={!!errors.email}
                    helperText={errors.email || t('contact.form.emailOptional')}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label={t('contact.form.message')}
                    multiline
                    rows={4}
                    value={formData.message}
                    onChange={handleInputChange('message')}
                    error={!!errors.message}
                    helperText={errors.message}
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                      },
                    }}
                  />
                </Grid>
                <Grid size={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={submitStatus === 'submitting'}
                    startIcon={submitStatus === 'submitting' ? null : <SendIcon />}
                    sx={{
                      px: 4,
                      py: 2,
                      background: 'linear-gradient(45deg, #4A90E2 30%, #5C6BC0 90%)',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 8px 25px rgba(74, 144, 226, 0.3)',
                      },
                      '&:disabled': {
                        background: 'rgba(255, 255, 255, 0.12)',
                      }
                    }}
                  >
                    {submitStatus === 'submitting' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ mr: 1 }} />
                        {t('contact.form.submitting')}
                      </Box>
                    ) : (
                      t('contact.form.submit')
                    )}
                  </Button>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>

        {/* Quick Order Section */}
        <Box id="quick-order-form" sx={{ mt: 12 }}>
          <QuickOrderSection />
        </Box>
      </Container>
    </Layout>
  );
};

export default ContactPage; 